import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MdDeleteOutline, MdInfoOutline, MdTrendingUp, MdTrendingDown, MdDateRange } from 'react-icons/md';
import { FiRepeat } from 'react-icons/fi';
import TickerPrice from '../../components/common/TickerPrice';

// Compact single-line variant of WatchListRow — identical data + interactions
// (row click → SmartTrade, profile, remove), just dense. The country flag is a
// faint left→right wash behind the identity (not a chip); the identity is a
// strict two-tier hierarchy: bold symbol + quiet secondary text.
const WatchListRowCompact = ({ inst, previousClose, onRemoveInstrument, onViewProfile }) => {
  const navigate = useNavigate();

  const handleRowClick = (e) => {
    if (e.target.closest('button')) return;
    navigate('/SmartTrade', { state: { instrument: inst } });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: '2-digit',
      });
    } catch (e) {
      return dateString;
    }
  };

  const hasLtp = inst.ltp != null;
  const isPositive = previousClose && inst.ltp != null
    ? inst.ltp >= previousClose
    : inst.change_percent >= 0;
  const displayValue = hasLtp ? inst.ltp : previousClose;
  const statusColor = hasLtp ? (isPositive ? 'text-profit' : 'text-loss') : 'text-fg-1';
  // theme-invariant profit/loss hex for the framer-motion color tween (see WatchListRow)
  const baseColorHex = hasLtp ? (isPositive ? '#00C292' : '#E46A76') : undefined;
  // For continuous, the resolved expiry lives on expiry_date; otherwise prefer active_expiry_date.
  const expiryDate = inst.is_continuous ? inst.expiry_date : (inst.active_expiry_date || inst.expiry_date);
  // Country flag → faint background wash (helps distinguish markets in "All"). Same source as the card.
  const flagUrl = (() => {
    const cc = inst.country_code;
    if (!cc || cc === 'ALL') return null;
    const code = cc.toLowerCase() === 'uk' ? 'gb' : cc.toLowerCase();
    return `https://flagcdn.com/w40/${code}.png`;
  })();

  return (
    <div
      onClick={handleRowClick}
      className="group relative overflow-hidden h-11 px-4 bg-bg-2 rounded-lg hover:bg-bg-3 transition-colors cursor-pointer"
    >
      {/* Country wash — faint flag fading left→right behind the identity */}
      {flagUrl && (
        <div className="wl-flag-wash" style={{ backgroundImage: `url(${flagUrl})` }} aria-hidden="true" />
      )}

      {/* Content sits above the wash */}
      <div className="relative z-[1] flex items-center gap-3 h-full">
        {/* Identity — symbol + company name together (bold symbol is the hero).
            pl-3 leaves room up front for the flag wash to peek through. */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1 pl-3">
          <span className="text-sm font-bold text-fg-1 shrink-0">{inst.symbol}</span>
          {inst.is_continuous && (
            <FiRepeat className="text-fg-3 shrink-0" size={11} title="Continuous contract" />
          )}
          {(inst.name || inst.description) && (
            <>
              <span className="text-fg-3/40 shrink-0">·</span>
              <span className="min-w-0 truncate text-xs text-fg-3">
                {inst.name || inst.description}
              </span>
            </>
          )}
        </div>

        {/* Exchange — its own quiet column */}
        <div className="w-20 shrink-0 truncate text-[10px] font-semibold uppercase tracking-wider text-fg-3">
          {inst.exchange}
        </div>

        {/* Meta: type (+ strike), expiry, and — for continuous — the rollover.
            Both dates matter: expiry = when the contract dies; roll = when it
            auto-switches (a few days before expiry). Hidden below lg. */}
        <div className="hidden lg:flex items-center gap-1.5 w-56 shrink-0 min-w-0 text-[11px] whitespace-nowrap">
          <span className="font-semibold uppercase tracking-wide text-fg-3 truncate">
            {inst.instrument_type || 'EQUITY'}
            {(inst.strike_price || inst.option_type)
              ? ` ${inst.strike_price || ''}${inst.option_type || ''}`
              : ''}
          </span>
          {expiryDate && (
            <>
              <span className="text-fg-3/40">·</span>
              <span className="inline-flex items-center gap-1 text-fg-3 shrink-0" title="Expiry date">
                <MdDateRange size={11} className="shrink-0" />
                {formatDate(expiryDate)}
              </span>
            </>
          )}
          {inst.is_continuous && (
            <>
              <span className="text-fg-3/40">·</span>
              <span className="inline-flex items-center gap-1 text-[var(--theme-purple)] shrink-0" title="Next rollover (a few days before expiry)">
                <FiRepeat size={10} className="shrink-0" />
                {inst.next_rollover_date
                  ? formatDate(inst.next_rollover_date)
                  : `${inst.rollover_days_before_expiry || 0}d`}
              </span>
            </>
          )}
        </div>

        {/* Algo status — hidden below md */}
        <div className="hidden md:flex items-center justify-center w-16 shrink-0">
          {inst.strategies_count > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-[var(--theme-purple)]" title={`${inst.strategies_count} active`}>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--theme-purple)] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--theme-purple)]" />
              </span>
              <span className="text-xs font-bold">{inst.strategies_count}</span>
            </span>
          ) : (
            <span className="text-[10px] font-medium text-fg-3">Idle</span>
          )}
        </div>

        {/* Price */}
        <div className="w-24 shrink-0 flex justify-end">
          <TickerPrice
            value={displayValue}
            currency={inst.currency}
            className={`text-sm font-bold ${statusColor}`}
            baseColor={baseColorHex}
          />
        </div>

        {/* % change */}
        <div className="w-20 shrink-0 flex justify-end">
          {hasLtp ? (
            <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${statusColor}`}>
              {isPositive ? <MdTrendingUp size={13} /> : <MdTrendingDown size={13} />}
              {Math.abs(inst.change_percent).toFixed(2)}%
            </span>
          ) : (
            <span className="text-xs text-fg-3">--</span>
          )}
        </div>

        {/* Actions (reveal on hover) */}
        <div className="w-[68px] shrink-0 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button
            onClick={() => onViewProfile(inst)}
            title="View company profile"
            className="h-7 w-7 flex items-center justify-center rounded-md text-fg-3 hover:bg-primary hover:text-white transition-colors"
          >
            <MdInfoOutline size={16} />
          </button>
          <button
            onClick={() => onRemoveInstrument(inst.instrument_id)}
            title="Remove instrument"
            className="h-7 w-7 flex items-center justify-center rounded-md text-fg-3 hover:bg-danger hover:text-white transition-colors"
          >
            <MdDeleteOutline size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WatchListRowCompact;
